import { useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import { Download, Building2, Award, Users, MapPin, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import PrivacyConsentNotice from "@/components/PrivacyConsentNotice";
import { trackFormLeadConversion } from "@/lib/adsTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const companyHighlights = [
  { icon: Building2, label: "Since 2014", description: "Established in 2014" },
  { icon: Users, label: "ISO 9001:2015 Certified", description: "International Standards" },
  { icon: MapPin, label: "50+ Cities", description: "Pan-India Presence" },
  { icon: Award, label: "ISO 9001:2015 Certified", description: "Quality Assured" },
];

const catalogContents = [
  "Company Overview & History",
  "Our Manufacturing Capabilities",
  "Product Categories & Range",
  "Industry Solutions",
  "Quality Certifications",
  "Client Success Stories",
  "Why Choose Ezeefit",
  "Contact Information",
];

export default function CataloguePage() {
  useCanonical("https://ezeefitofficesystems.in/catalogue");
  useMetaDescription("Download Ezeefit's company profile — modular office furniture manufacturer since 2014. Product range, manufacturing & industry solutions for corporates.");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Record the download request
      await axios.post(`${API}/enquiries`, {
        company_name: formData.company,
        contact_person: formData.name,
        email: formData.email,
        phone: formData.phone,
        industry: "Catalogue Download",
        project_location: "N/A",
        seats_planned: "N/A",
        timeline: "N/A",
        budget_range: "N/A",
        design_support: "No",
        message: "Requested company profile download"
      });
      trackFormLeadConversion({ formName: "catalogue_download" });

      // Simulate PDF download (in production, this would be a real PDF)
      const profileContent = generateCompanyProfile();
      const blob = new Blob([profileContent], { type: "text/html" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Ezeefit-Company-Profile.html";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setIsDownloaded(true);
      toast.success("Download started! Check your downloads folder.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateCompanyProfile = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <title>Ezeefit Company Profile</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #0F172A; }
    h1 { color: #F97316; border-bottom: 3px solid #F97316; padding-bottom: 10px; }
    h2 { color: #0F172A; margin-top: 30px; }
    .highlight { background: #F8FAFC; padding: 20px; margin: 20px 0; border-left: 4px solid #F97316; }
    .stats { display: flex; gap: 20px; margin: 20px 0; }
    .stat { background: #0F172A; color: white; padding: 20px; text-align: center; flex: 1; }
    .stat-number { font-size: 24px; color: #F97316; font-weight: bold; }
    ul { line-height: 2; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #E2E8F0; text-align: center; }
  </style>
</head>
<body>
  <h1>Ezeefit Office Systems - Company Profile</h1>
  <p><em>A Division of Ezeefit Modular Furniture Pvt. Ltd.</em></p>
  
  <div class="stats">
    <div class="stat"><div class="stat-number">26+</div>Years Experience</div>
    <div class="stat"><div class="stat-number">ISO 9001:2015</div>Certified</div>
    <div class="stat"><div class="stat-number">50+</div>Cities Covered</div>
    <div class="stat"><div class="stat-number">Mumbai</div>Own Factory</div>
  </div>

  <h2>About Us</h2>
  <p>Since 2014, Ezeefit has been India's trusted partner for modular office furniture solutions. We specialize in delivering scalable workspace furniture for growing corporates, from concept to installation—all under one roof.</p>

  <div class="highlight">
    <strong>Our Mission:</strong> To deliver modular furniture solutions that enhance productivity, comfort, and aesthetics in every workspace we touch.
  </div>

  <h2>Our Strengths</h2>
  <ul>
    <li><strong>Since 2014 Experience</strong> - Deep expertise in corporate furniture execution</li>
    <li><strong>In-House Manufacturing</strong> - Complete control over quality and timelines</li>
    <li><strong>ISO 9001:2015 Certified</strong> - International quality standards</li>
    <li><strong>Pan-India Delivery</strong> - Coverage across 50+ cities</li>
    <li><strong>Dedicated Technical Team</strong> - CAD design to installation support</li>
    <li><strong>Customization Capability</strong> - Tailored solutions for every requirement</li>
  </ul>

  <h2>Products & Services</h2>
  <ul>
    <li>Linear & L-Shaped Workstations</li>
    <li>Executive Cabins & Desks</li>
    <li>Conference & Meeting Tables</li>
    <li>Height-Adjustable Desks</li>
    <li>Phone Booths & Meeting Pods</li>
    <li>Filing & Storage Solutions</li>
    <li>Ergonomic Office Chairs</li>
    <li>Reception Counters</li>
    <li>Acoustic Panels & Partitions</li>
  </ul>

  <h2>Industry Solutions</h2>
  <p>We provide specialized solutions for:</p>
  <ul>
    <li>IT & Technology Companies</li>
    <li>Corporate Headquarters</li>
    <li>Co-working Spaces</li>
    <li>BPO & Call Centers</li>
    <li>Banking & Financial Institutions</li>
    <li>Public Sector Offices</li>
    <li>Manufacturing & Industrial Offices</li>
    <li>Startups & Scale-ups</li>
  </ul>

  <h2>Why Choose Ezeefit?</h2>
  <ul>
    <li>End-to-end execution from design to installation</li>
    <li>Competitive pricing with premium quality</li>
    <li>Quick turnaround times</li>
    <li>After-sales service and AMC support</li>
    <li>Architect & consultant partnership programs</li>
    <li>White-label solutions available</li>
  </ul>

  <div class="footer">
    <h2>Contact Us</h2>
    <p><strong>Ezeefit Office Systems</strong></p>
    <p>Mumbai, Maharashtra, India</p>
    <p>Phone: 9820596811 / 9820042100</p>
    <p>Email: hello@ezeefitofficesystems.in / eos@ezeefitofficesystems.in</p>
    <p>Website: ezeefitofficesystems.in</p>
  </div>
</body>
</html>
    `;
  };

  return (
    <div className="pt-20" data-testid="catalogue-page">
      <Helmet>
        <meta name="description" content="Download Ezeefit's company profile — modular office furniture manufacturer since 2014. Product range, manufacturing & industry solutions for corporates." />
      </Helmet>
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Free Download
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Ezeefit Company Profile
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
              Learn about our years of expertise, capabilities, and how we can transform your workspace.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Highlights */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {companyHighlights.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <item.icon className="w-10 h-10 text-[#F97316] mx-auto mb-3" />
                <div className="font-bold text-[#0F172A]">{item.label}</div>
                <div className="text-sm text-[#64748B]">{item.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* What's Inside */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">
                What's Inside the Profile
              </h2>
              <p className="text-lg text-[#64748B] mb-8">
                Our company profile gives you a comprehensive overview of Ezeefit's capabilities, 
                expertise, and how we can support your office furniture needs.
              </p>

              <div className="space-y-4">
                {catalogContents.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#F97316] flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#334155]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Download Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-[#E2E8F0] p-8"
            >
              {isDownloaded ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Download className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-3">Download Started!</h3>
                  <p className="text-[#64748B] mb-6">
                    Thank you for your interest. Check your downloads folder for the company profile.
                  </p>
                  <Button
                    onClick={() => setIsDownloaded(false)}
                    variant="outline"
                  >
                    Download Again
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-2">
                    Download Company Profile
                  </h3>
                  <p className="text-[#64748B] mb-6">
                    Fill in your details to receive instant access.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Full Name *
                      </label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        className="h-12 rounded-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Work Email *
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@company.com"
                        className="h-12 rounded-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Company Name *
                      </label>
                      <Input
                        required
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Your company"
                        className="h-12 rounded-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#334155] mb-2">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="9820042100"
                        className="h-12 rounded-none"
                      />
                    </div>
                    <PrivacyConsentNotice className="mb-2" />

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 bg-[#F97316] hover:bg-orange-600 text-white rounded-none font-semibold"
                    >
                      {isSubmitting ? (
                        "Processing..."
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Download Free Profile
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-xs text-[#94A3B8] mt-4 text-center">
                    By downloading, you agree to receive updates from Ezeefit. 
                    We respect your privacy.
                  </p>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
            Ready to Discuss Your Project?
          </h2>
          <p className="text-[#64748B] mb-6">
            Our team is ready to help you create the perfect workspace.
          </p>
          <Button
            asChild
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-none px-8 py-6"
          >
            <a href="/contact">
              Request Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
